import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import throttle from 'lodash/throttle';

import { isServerSide } from '@r1-frontend/shared/helpers/ssr';
import { useMatchMedia } from '@r1-frontend/shared/hooks/useMatchMedia';

import * as ST from './styled';

const texts = [
    {
        id: 1,
        title: 'Камера с инфракрасной подсветкой',
        text: 'Рассмотрите всех так же чётко, как и ночью',
        image_webp: {
            mobile: require('~/static/images/domofon/presentation/mobile/domofon-1.webp'),
            desktop: require('~/static/images/domofon/presentation/desktop/domofon-1.webp'),

        },
        image_png: {
            mobile: require('~/static/images/domofon/presentation/mobile/domofon-1.png'),
            desktop: require('~/static/images/domofon/presentation/desktop/domofon-1.png'),

        },
    },
    {
        id: 2,
        title: 'Металлический антивандальный корпус',
        text: 'Не нужно беспокоиться за сохранность домофона',
        image_webp: {
            mobile: require('~/static/images/domofon/presentation/mobile/domofon-2.webp'),
            desktop: require('~/static/images/domofon/presentation/desktop/domofon-2.webp'),

        },
        image_png: {
            mobile: require('~/static/images/domofon/presentation/mobile/domofon-2.png'),
            desktop: require('~/static/images/domofon/presentation/desktop/domofon-2.png'),

        },
    },
    {
        id: 3,
        title: 'Видео в HD качестве',
        text: 'Увидите лица всех, кто хочет попасть внутрь',
        image_webp: {
            mobile: require('~/static/images/domofon/presentation/mobile/domofon-3.webp'),
            desktop: require('~/static/images/domofon/presentation/desktop/domofon-3.webp'),

        },
        image_png: {
            mobile: require('~/static/images/domofon/presentation/mobile/domofon-3.png'),
            desktop: require('~/static/images/domofon/presentation/desktop/domofon-3.png'),

        },
    },
];

const MIN_DISTANCE = 112;

const LAG_DISTANCE = 200;

const getVisible = (id: number, currentStep: number, length: number): boolean => {
    let step = currentStep > length ? length : currentStep;
    step = step < 1 ? 1 : step;
    return id === step;
};

export enum EMediaWidth {
    mobile = '(max-width: 767px)',
}

export const PresentationDomofon = (): JSX.Element => {
    const isMobile = useMatchMedia(EMediaWidth.mobile);
    const urlPath = useMemo(()=> isMobile ?  'mobile' : 'desktop', [isMobile]);
    const prevPageY = useRef<number>(isServerSide() ? 0 : window.pageYOffset);

    const [currentStep, setCurrentStep] = useState(0);

    const presentationRef = useRef<HTMLDivElement | null>(null);

    const onScroll = useCallback(throttle(() => {
        if (!presentationRef?.current) {
            return;
        }

        const maxStep = texts.length;
        const parentBlock = presentationRef.current;
        const position = parentBlock.getBoundingClientRect();
        const clientHeight = document.documentElement.clientHeight;
        const elementTop = position.top;
        const elementBottom = position.bottom;

        const toBottom = window.pageYOffset > prevPageY.current;

        if (toBottom) {
            if (elementTop <= MIN_DISTANCE) {
                setCurrentStep((value) => {
                    const newValue = value + 1 > maxStep ? maxStep : value + 1;
                    const newStep = elementBottom < clientHeight ? maxStep + 1 : newValue;

                    // Подстраиваем высоту родительского блока, чтобы небыло пустых прокруток.
                    if (newStep === maxStep) {
                        parentBlock.style.height = `${clientHeight + Math.abs(elementTop) + LAG_DISTANCE}px`;
                    }

                    return newStep;
                });
            } else {
                setCurrentStep(0);
            }
        } else if (elementBottom >= clientHeight) {
            setCurrentStep((value) => {
                const newValue = value - 1 < 0 ? 0 : value - 1;
                const newStep = elementTop > MIN_DISTANCE ? 0 : newValue;

                if (newStep === 0) {
                    parentBlock.style.height = ST.START_HEIGHT;
                }

                return newStep;
            });
        }

        prevPageY.current = window.pageYOffset;
    }, 900, { leading: false }), [presentationRef]);

    useEffect(() => {
        window.addEventListener('scroll', onScroll);

        return () => {
            window.removeEventListener('scroll', onScroll);
        };
    }, [onScroll]);

    return (
        <ST.PresentationDomofon ref={presentationRef}>
            <ST.InnerWrap>
                <ST.GridContainer>
                    <ST.Description>
                        {texts.map((item) => {
                            const isVisible = getVisible(item.id, currentStep, texts.length);

                            return (
                                <ST.TextBlock
                                    key={item.id}
                                    isVisible={isVisible}
                                >
                                    <ST.Title>{item.title}</ST.Title>
                                    <ST.Text>{item.text}</ST.Text>
                                </ST.TextBlock>
                            );
                        })}
                    </ST.Description>
                    <ST.ImageWrap>
                        {texts.map((item) => {
                            const isVisible = getVisible(item.id, currentStep, texts.length);

                            return (
                                <ST.Picture
                                    key={item.id}
                                    isVisible={isVisible}
                                >
                                    <source srcSet={item.image_webp[urlPath]} type='image/webp'/>
                                    <ST.Img
                                        src={item.image_png[urlPath]}
                                        isVisible={isVisible}
                                    />
                                </ST.Picture>
                            );
                        })}
                    </ST.ImageWrap>
                </ST.GridContainer>
            </ST.InnerWrap>
        </ST.PresentationDomofon>
    );
};
